/*
 * Replay & repair — narrow scoped replay (record / program / period /
 * org-unit) plus a full-replay escape hatch. Uses existing
 * /api/replay/run + /api/replay/full endpoints. Lists recent replay
 * jobs so the operator can verify their action took effect.
 */

import { useEffect, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import StatusBadge from "../components/StatusBadge.js";
import { api, ApiError } from "../lib/api.js";

type Scope = "full" | "record" | "program" | "period" | "orgUnit";

export default function ReplayRepair() {
  const [scope, setScope] = useState<Scope>("record");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [jobs, setJobs] = useState<Array<{ id: string; scope: string; status: string; processedMutations: number; startedAt: string; completedAt?: string }>>([]);

  const refresh = () => {
    api.replayJobs()
      .then((r) => setJobs(r.jobs as unknown as typeof jobs))
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  };
  useEffect(refresh, []);

  async function run() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const body: Record<string, unknown> = { scope };
      if (scope === "record") body.sourceRecordId = value;
      if (scope === "program") body.programId = value;
      if (scope === "period") body.periodId = value;
      if (scope === "orgUnit") body.orgUnitId = value;
      if (scope !== "full" && !value.trim()) {
        setError("Enter the target id for this scope.");
        setBusy(false);
        return;
      }
      const r = await api.runReplay(body);
      setNotice(`Replay queued (${r.job.id}). Refresh below to see status.`);
      setTimeout(refresh, 1000);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Replay &amp; repair</h1>
          <p className="muted">
            Replay scoped windows of mutations to rebuild downstream state. Use this when a metadata change
            invalidates prior contributions, or when a bug shipped values you need to recompute against the
            corrected logic.
          </p>
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}
      {notice && <div className="ts-alert ts-alert-info">{notice}</div>}

      <section className="empty-state-card" style={{ marginBottom: 16 }}>
        <h2 style={{ marginTop: 0 }}>Trigger a replay</h2>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <label className="transfer-filter">
            Scope
            <select value={scope} onChange={(e) => setScope(e.target.value as Scope)}>
              <option value="record">Single record</option>
              <option value="program">Program</option>
              <option value="period">Period</option>
              <option value="orgUnit">Org unit</option>
              <option value="full">Full replay (everything)</option>
            </select>
          </label>
          {scope !== "full" && (
            <label className="transfer-filter transfer-filter-grow">
              Target ID
              <input
                type="text"
                placeholder={
                  scope === "record" ? "sourceRecordId"
                  : scope === "program" ? "programId"
                  : scope === "period" ? "periodId (e.g. 202504)"
                  : "orgUnitId"
                }
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            </label>
          )}
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void run()}>
            {busy ? "Queuing…" : "Replay now"}
          </button>
        </div>
        {scope === "full" && (
          <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
            ⚠ Full replay rebuilds every bucket from the journal. Expect minutes to hours depending on workspace size.
          </p>
        )}
      </section>

      <section>
        <div className="section-head">
          <h2>Recent replay jobs</h2>
          <button type="button" className="btn btn-ghost btn-sm" onClick={refresh}>Refresh</button>
        </div>
        {jobs.length === 0 ? (
          <div className="empty-state-card">
            <h2 style={{ marginTop: 0 }}>No replay jobs yet</h2>
            <p>Trigger one above. Replay jobs land here with mutation count + status.</p>
          </div>
        ) : (
          <table className="transfer-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Scope</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Mutations</th>
                <th>Started</th>
                <th>Completed</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id}>
                  <td className="mono" style={{ fontSize: 11 }}>{j.id}</td>
                  <td><span className="pill pill-muted">{j.scope}</span></td>
                  <td><StatusBadge status={j.status} /></td>
                  <td style={{ textAlign: "right" }}>{j.processedMutations}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{j.startedAt}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{j.completedAt ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
